import React, { useState, useEffect, useRef } from 'react';
import { FiMapPin, FiLoader } from 'react-icons/fi';
import api from '../services/api';
import './LocationAutocomplete.css';

const LocationAutocomplete = ({ value, onChange, placeholder, className = '' }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [predictions, setPredictions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [userLocation, setUserLocation] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);

  // Uberlândia coordinates as fallback
  const UBERLANDIA_COORDS = { lat: -18.9188, lng: -48.2766 };

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Get user's location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          // If geolocation fails, use Uberlândia as fallback
          console.log('Geolocation not available, using Uberlândia as fallback');
          setUserLocation(UBERLANDIA_COORDS);
        },
        { timeout: 5000 }
      );
    } else {
      // If geolocation is not supported, use Uberlândia as fallback
      setUserLocation(UBERLANDIA_COORDS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPredictions = async (input) => {
    if (!input || input.trim().length < 2) {
      setPredictions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Build params object with location bias if available
      const params = {
        input: input.trim(),
        language: 'pt-BR'
      };

      // Add location bias to prioritize nearby results
      if (userLocation) {
        params.lat = userLocation.lat;
        params.lng = userLocation.lng;
      }

      const response = await api.get('/api/properties/autocomplete', {
        params
      });

      setPredictions(response.data.predictions || []);
      setShowDropdown(true);
    } catch (error) {
      console.error('Error fetching predictions:', error);
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedIndex(-1);

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounce
    debounceTimer.current = setTimeout(() => {
      fetchPredictions(newValue);
    }, 300);
  };

  const handleSelectPrediction = (prediction) => {
    const mainText = prediction.structured_formatting?.main_text || prediction.description;
    setInputValue(mainText);
    setShowDropdown(false);
    setPredictions([]);
    setSelectedIndex(-1);

    if (onChange) {
      onChange({
        description: prediction.description,
        placeId: prediction.place_id,
        mainText: mainText,
        secondaryText: prediction.structured_formatting?.secondary_text || ''
      });
    }
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < predictions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && predictions[selectedIndex]) {
          handleSelectPrediction(predictions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    if (predictions.length > 0) {
      setShowDropdown(true);
    }
  };

  return (
    <div className={`location-autocomplete ${className}`} ref={dropdownRef}>
      <div className="autocomplete-input-wrapper">
        <FiMapPin className="autocomplete-icon" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder || 'Digite um local...'}
          className="autocomplete-input"
        />
        {loading && (
          <FiLoader className="autocomplete-loading" />
        )}
      </div>

      {showDropdown && predictions.length > 0 && (
        <div className="autocomplete-dropdown">
          {predictions.map((prediction, index) => (
            <div
              key={prediction.place_id}
              className={`autocomplete-item ${
                index === selectedIndex ? 'selected' : ''
              }`}
              onClick={() => handleSelectPrediction(prediction)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <FiMapPin className="item-icon" />
              <div className="item-text">
                <div className="item-main">
                  {prediction.structured_formatting?.main_text || prediction.description}
                </div>
                {prediction.structured_formatting?.secondary_text && (
                  <div className="item-secondary">
                    {prediction.structured_formatting.secondary_text}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
